import { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { toastr } from 'react-redux-toastr';
import DelinquencyServiceV2 from '../../services/delinquencyServiceV2';
import {
  useTableFilterSortSearchManager,
  useTableFilterSortData,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils/index';
import { PROPERTY_PATH_MAP, HEADERS, TOTAL_COLUMNS } from './constantsv2';
import { normalizeDelinquencyRows } from './utilsV2';
import { exportAndDownloadCSV } from '../../utils/csv-helpers';
import { cond, equals, always, anyPass, T } from 'ramda';
import { getUrlWithSelectedPropertyId } from '../../utils/navigation-helpers';
import ActivityTypeService from '../../services/activityTypeService';
import ActivityService from '../../services/activityService';
import { createActivity } from '../../components/CreateActivityModal';
import peopleProfileMessages from '../PeopleProfile/messages';
import useProspectAssignees from '../../hooks/data-fetching/useProspectAssignees';
import useActivityTypes from '../../hooks/data-fetching/useActivityTypes';
import useUniqueTableName from '../../hooks/useUniqueTableName';
import useActionsFirstColumnOrder from '../../hooks/useActionsFirstColumnOrder';

const CSV_EXCLUDED_HEADER_IDS = ['actions'];

/**
 * Returns the id of the first column that the CSV export writes. The export drops
 * every column in CSV_EXCLUDED_HEADER_IDS, so the totals label must go in the first
 * column that survives that filter, not in the first column of the table.
 */
export const getFirstExportedColumnId = (headers = []) =>
  headers.find(({ id }) => !CSV_EXCLUDED_HEADER_IDS.includes(id))?.id;

export const useDelinquentAndPrepaid = ({
  selectedProperty,
  NAME,
  intl,
  locale,
  store,
}) => {
  const name = useUniqueTableName(NAME);
  const {
    data,
    isLoading,
    refetch: refreshTable,
  } = useFetchDelinquentAndPrepaid({
    selectedProperty,
  });
  const results = useMemo(() => normalizeDelinquencyRows(data ?? []), [data]);

  const { showCAMOnlyBalances, showSubsidyOnlyBalances } =
    useGetShowOptionalColumns(results);

  const { prospectAssignees: users } = useProspectAssignees();

  const assigneeOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        text: `${u.firstName} ${u.lastName}`,
      })),
    [users],
  );

  const { activityTypes } = useActivityTypes();
  const { activityTypesList } = useMemo(() => {
    const activityTypeService = new ActivityTypeService();
    const activityTypesList = activityTypeService.generateActivityTypesList({
      activityTypes,
      locale,
      intl,
    });

    return { activityTypeService, activityTypesList };
  }, [activityTypes, intl, locale]);

  const { statusOptions, floorPlanOptions, buildingNumberOptions } =
    useGetFilterOptions({
      results,
    });
  const headers = useHeaders({
    statusOptions,
    floorPlanOptions,
    buildingNumberOptions,
    showSubsidyOnlyBalances,
    showCAMOnlyBalances,
  });

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    headers,
    initialOrderBy: 'unitNumber',
    initialOrder: 'ASC',
  });

  const sortedAndFilteredResults = useTableFilterSortData({
    results,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  const { organizationId } = selectedProperty;

  const handleCreateActivity = async ({
    intl,
    organizationId,
    refreshTable,
    newActivity,
  }): Promise<void> => {
    try {
      const activityService = new ActivityService();

      await activityService.save(newActivity, organizationId);

      toastr.success(
        intl.formatMessage(peopleProfileMessages.successHeader),
        intl.formatMessage(
          peopleProfileMessages.successActivityCreateDescription,
        ),
      );

      refreshTable();
    } catch (err) {
      toastr.error(
        intl.formatMessage(peopleProfileMessages.errorHeader),
        err.toString(),
      );
    }
  };

  const handleCreateActivityClick = useCallback(
    async (row) => {
      const delinquency = row;
      const prospectInfo = {
        id: delinquency.prospectId,
        firstName: delinquency.firstName,
        lastName: delinquency.lastName,
        createdAt: delinquency.prospectCreatedAt,
      };

      const getStageProps = cond([
        [
          equals('Applicant'),
          always({ stage: 'applicant', urlId: delinquency.applicationId }),
        ],
        [
          anyPass([equals('Current Resident'), equals('Prior Resident')]),
          always({ stage: 'resident', urlId: delinquency.residentId }),
        ],
        [
          anyPass([equals('Prospect'), T]),
          always({ stage: 'prospect', urlId: delinquency.prospectId }),
        ],
      ]);
      const stageProps = getStageProps(delinquency.customerStatus);

      const newActivity = await createActivity({
        intl,
        activityTypesList,
        store,
        prospect: prospectInfo,
        assigneeList: assigneeOptions,
        showViewProfileBtn: true,
        ...stageProps,
      });

      await handleCreateActivity({
        intl,
        organizationId,
        refreshTable,
        newActivity: {
          ...newActivity,
          customerStatus: delinquency.customerStatus ?? 'Prospect',
        },
      });
    },
    [
      intl,
      activityTypesList,
      assigneeOptions,
      store,
      organizationId,
      refreshTable,
    ],
  );

  const rows = useRows({
    sortedAndFilteredResults,
    intl,
    locale,
    store,
    handleCreateActivityClick,
  });

  const { initialColumnsOrder, shouldForceOverwrite } =
    useActionsFirstColumnOrder({ name, headers });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
    initialColumnsOrder,
    forceOverwrite: shouldForceOverwrite,
  });

  const footerRow = useGetFooterRow({
    filteredHeaders,
    sortedAndFilteredResults,
  });

  const { onCSVButtonClick } = useCSVPDFExport({
    propertyName: name,
    hasAnyFilters:
      Object.keys(filterState)?.length ||
      Object.keys(dateState)?.length ||
      Object.keys(searchState)?.length,
    filteredHeaders,
    rows,
    footerRow,
    results: sortedAndFilteredResults,
  });

  return {
    allColumnsHidden,
    columnOptions,
    count: rows?.length ?? 0,
    dateState,
    filterState,
    filterTypeState,
    filteredHeaders,
    footerRow,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    headers,
    isLoading,
    name,
    order,
    orderBy,
    rows,
    searchState,
    selectedColumns,
    onCSVButtonClick,
    totalCount: results?.length ?? 0,
  };
};

export const useFetchDelinquentAndPrepaid = ({ selectedProperty = {} }) => {
  const { organizationId, id } = selectedProperty;

  const delinquencyService = useMemo(() => new DelinquencyServiceV2(), []);
  const abortController = new AbortController();
  const queryKey = [organizationId, id];
  const options = { signal: abortController.signal };
  return useQuery(
    ['DelinquentAndPrepaid', queryKey],
    () =>
      delinquencyService.getAllDelinquencies({
        organizationId,
        propertyId: id,
        options,
      }),
    { enabled: true },
  );
};

export const useGetFilterOptions = ({ results }) => {
  return useMemo(() => {
    const options = {
      statusOptions: [],
      floorPlanOptions: [],
      buildingNumberOptions: [],
      propertyNameOptions: [],
    };
    const statusOptions = new Set();
    const floorPlanOptions = new Set();
    const buildingNumberOptions = new Set();
    const propertyNameOptions = new Set();

    if (results) {
      results.forEach((row) => {
        if (row?.status?.length) {
          statusOptions.add(row.status);
        }
        if (row?.floorPlan?.length) {
          floorPlanOptions.add(row.floorPlan);
        }
        if (row?.buildingNumber?.length) {
          buildingNumberOptions.add(row.buildingNumber);
        }

        if (row?.propertyName?.length) {
          propertyNameOptions.add(row.propertyName);
        }
      });

      options.statusOptions = Array.from(statusOptions)
        .sort()
        .map((status) => ({
          text: status,
          value: status?.toLowerCase(),
        }));

      options.floorPlanOptions = Array.from(floorPlanOptions)
        .sort()
        .map((floorPlan) => ({
          text: floorPlan,
          value: floorPlan?.toLowerCase(),
        }));

      options.buildingNumberOptions = Array.from(buildingNumberOptions)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .map((buildingNumber) => ({
          text: buildingNumber,
          value: buildingNumber?.toLowerCase(),
        }));

      options.propertyNameOptions = Array.from(propertyNameOptions)
        .sort()
        .map((propertyName) => ({
          text: propertyName,
          value: propertyName?.toLowerCase(),
        }));
    }

    return options;
  }, [results]);
};

export const useHeaders = ({
  statusOptions = {},
  floorPlanOptions = {},
  buildingNumberOptions = {},
  propertyNameOptions = [],
  showSubsidyOnlyBalances,
  showCAMOnlyBalances,
  centralizedDelinquency = false,
}) => {
  return useMemo(() => {
    const subsidyColumnIds = [
      'subsidyOnlyBalance',
      'subsidyDelinquent',
      'subsidyPrepaid',
    ];

    return HEADERS.filter(
      (header) =>
        (!subsidyColumnIds.includes(header.id) &&
          header.id !== 'CAMOnlyBalance' &&
          header.id !== 'propertyName') ||
        (subsidyColumnIds.includes(header.id) &&
          (showSubsidyOnlyBalances || centralizedDelinquency)) ||
        (header.id === 'CAMOnlyBalance' && showCAMOnlyBalances) ||
        (header.id === 'propertyName' && centralizedDelinquency),
    ).map((header) => {
      if (header.id === 'status') {
        header.filterOptions = statusOptions;
      }
      if (header.id === 'floorPlan') {
        header.filterOptions = floorPlanOptions;
      }
      if (header.id === 'buildingNumber') {
        header.filterOptions = buildingNumberOptions;
      }
      if (header.id === 'propertyName') {
        header.filterOptions = propertyNameOptions;
      }
      return header;
    });
  }, [
    statusOptions,
    floorPlanOptions,
    buildingNumberOptions,
    showSubsidyOnlyBalances,
    showCAMOnlyBalances,
    centralizedDelinquency,
    propertyNameOptions,
  ]);
};

export const useGetShowOptionalColumns = (
  rows: Array<any>,
  { centralized = false }: { centralized?: boolean } = {},
) => {
  return useMemo(() => {
    const someRowsHaveCAM = rows.some((row) =>
      Object.prototype.hasOwnProperty.call(row, 'CAMOnlyBalance'),
    );
    const someRowsHaveSubsidy = centralized
      ? rows.some((row) => row.includeSubsidySubjournal)
      : rows.some((row) =>
          Object.prototype.hasOwnProperty.call(row, 'subsidyOnlyBalance'),
        );
    const showCAMOnlyBalances = someRowsHaveCAM;
    const showSubsidyOnlyBalances = centralized
      ? true
      : someRowsHaveSubsidy || !someRowsHaveCAM;

    return { showCAMOnlyBalances, showSubsidyOnlyBalances };
  }, [rows, centralized]);
};

export const useGetFooterRow = ({
  filteredHeaders,
  sortedAndFilteredResults,
}) => {
  return useMemo(() => {
    const totalsColumns = JSON.parse(JSON.stringify(TOTAL_COLUMNS));

    sortedAndFilteredResults.forEach((result) => {
      Object.keys(totalsColumns).forEach((id) => {
        const rawValue = result[id];
        if (rawValue == null || rawValue === '') {
          return;
        }

        const addition =
          typeof rawValue === 'string' ? parseFloat(rawValue) : rawValue;
        if (!Number.isNaN(addition)) {
          totalsColumns[id].value += addition;
        }
      });
    });

    return filteredHeaders.reduce((object, { id }) => {
      object[id] = totalsColumns[id] ?? '';
      return object;
    }, {});
  }, [filteredHeaders, sortedAndFilteredResults]);
};

export const useRows = ({
  sortedAndFilteredResults,
  handleCreateActivityClick,
  isCentralizedRows = false,
}) => {
  function addPropertyIdToUrl(propertyId, url, goBack = true) {
    return `/property/${propertyId}${url}${goBack ? '?prev=dq' : ''}`;
  }
  return useMemo(
    () =>
      sortedAndFilteredResults?.map((row) => {
        const {
          id,
          unitNumber,
          status,
          floorPlan,
          buildingNumber,
          profile,
          residentId,
          applicationId,
          phoneNumber,
          moveInDate,
          moveOutDate,
          collectionsNotes,
          underEviction,
          prepaidBalance,
          delinquentBalance,
          writtenOff,
          subsidyOnlyBalance,
          subsidyDelinquent,
          subsidyPrepaid,
          CAMOnlyBalance,
          residentOnlyBalance,
          residentDelinquent,
          residentPrepaid,
          rentOnlyBalance,
          current,
          thirtyOneToSixty,
          sixtyOneToNinety,
          ninetyOnePlus,
          depositsHeld,
          depositsOwed,
          lateFees,
          nsfFees,
          unitId,
          floorPlanId,
          propertyName,
          propertyId,
          includeSubsidySubjournal,
        } = row;

        let profileLink;
        let url;

        if (residentId && status === 'Prior Resident') {
          url = `/prior-resident/${residentId}`;
        } else if (residentId) {
          url = `/resident/${residentId}`;
        } else {
          url = `/application/${applicationId}`;
        }

        profileLink = isCentralizedRows
          ? addPropertyIdToUrl(propertyId, url, false)
          : getUrlWithSelectedPropertyId(url);

        const unitURL = `/unit/${unitId}`;
        const floorPlanURL = `/floorPlan/${floorPlanId}`;
        return {
          id,
          propertyName: {
            variant: propertyName?.length > 10 ? 'description' : 'string',
            value: propertyName,
          },
          unitNumber: {
            variant: 'link',
            to: isCentralizedRows
              ? addPropertyIdToUrl(propertyId, unitURL)
              : getUrlWithSelectedPropertyId(unitURL),
            target: '_blank',
            value: unitNumber,
          },
          status: {
            value: status,
          },
          floorPlan: {
            variant: 'link',
            to: isCentralizedRows
              ? addPropertyIdToUrl(propertyId, floorPlanURL)
              : getUrlWithSelectedPropertyId(floorPlanURL),
            target: '_blank',
            value: floorPlan,
          },
          buildingNumber: {
            variant: buildingNumber?.length > 10 ? 'description' : 'string',
            value: buildingNumber,
          },
          profile: {
            variant: 'link',
            to: profileLink,
            target: '_blank',
            value: profile,
            csvContent: (profile ?? '').replaceAll(/[,\r\n]/g, ''),
          },
          phoneNumber: {
            variant: 'phoneNumber',
            value: phoneNumber,
          },
          moveInDate: {
            variant: 'date',
            value: moveInDate,
          },
          moveOutDate: {
            variant: 'date',
            value: moveOutDate,
          },
          collectionsNotes: {
            variant: 'description',
            value: collectionsNotes,
            csvContent: (collectionsNotes ?? '').replaceAll(/[,\r\n]/g, ''),
          },
          underEviction: {
            variant: 'boolean',
            value: underEviction,
            iconName: 'AlertInfoIcon',
            color: 'error',
          },
          prepaidBalance: {
            variant: 'currency',
            value: prepaidBalance ?? 0,
          },
          delinquentBalance: {
            variant: 'currency',
            value: delinquentBalance ?? 0,
          },
          writtenOff: {
            variant: 'currency',
            value: writtenOff ?? 0,
          },
          subsidyOnlyBalance: {
            variant: 'currency',
            value:
              isCentralizedRows && !includeSubsidySubjournal
                ? ''
                : subsidyOnlyBalance ?? 0,
          },
          subsidyDelinquent: {
            variant: 'currency',
            value:
              isCentralizedRows && !includeSubsidySubjournal
                ? ''
                : subsidyDelinquent ?? '',
          },
          subsidyPrepaid: {
            variant: 'currency',
            value:
              isCentralizedRows && !includeSubsidySubjournal
                ? ''
                : subsidyPrepaid ?? '',
          },
          CAMOnlyBalance: {
            variant: 'currency',
            value: CAMOnlyBalance ?? 0,
          },
          residentOnlyBalance: {
            variant: 'currency',
            value: residentOnlyBalance ?? 0,
          },
          residentDelinquent: {
            variant: 'currency',
            value: residentDelinquent ?? '',
          },
          residentPrepaid: {
            variant: 'currency',
            value: residentPrepaid ?? '',
          },
          rentOnlyBalance: {
            variant: 'currency',
            value: rentOnlyBalance ?? 0,
          },
          current: {
            variant: 'currency',
            value: current ?? 0,
          },
          thirtyOneToSixty: {
            variant: 'currency',
            value: thirtyOneToSixty ?? 0,
          },
          sixtyOneToNinety: {
            variant: 'currency',
            value: sixtyOneToNinety ?? 0,
          },
          ninetyOnePlus: {
            variant: 'currency',
            value: ninetyOnePlus ?? 0,
          },
          depositsHeld: {
            variant: 'currency',
            value: depositsHeld ?? 0,
          },
          depositsOwed: {
            variant: 'currency',
            value: depositsOwed ?? 0,
          },
          lateFees: {
            variant: 'number',
            value: lateFees === 0 ? '' : lateFees,
          },
          nsfFees: {
            variant: 'number',
            value: nsfFees === 0 ? '' : nsfFees,
          },
          actions: {
            variant: 'menu',
            name: 'actionsMenu',
            iconName: 'BoltIcon',
            color: 'primary',
            options: [
              {
                text: 'Create Activity',
                value: residentId,
                onClick: () => {
                  handleCreateActivityClick(row);
                },
              },
            ],
          },
        };
      }),
    [sortedAndFilteredResults, handleCreateActivityClick, isCentralizedRows],
  );
};

export const useCSVPDFExport = ({
  filteredHeaders,
  rows,
  footerRow,
  propertyName,
  hasAnyFilters,
}) => {
  const onCSVButtonClick = useCallback(() => {
    const csvRows = rows.map((row) => {
      const { profile, collectionsNotes, underEviction } = row;
      const csvRow = {
        ...row,
        profile: {
          ...profile,
          value: profile.csvContent,
        },
        collectionsNotes: {
          ...collectionsNotes,
          value: collectionsNotes.csvContent,
        },
        underEviction: {
          ...underEviction,
          value: underEviction.value ? 'Yes' : '',
          variant: 'description',
        },
      };

      return csvRow;
    });

    const firstColumn = getFirstExportedColumnId(filteredHeaders);
    const totalsRow = {
      ...footerRow,
      [firstColumn]: {
        variant: 'string',
        value: 'Totals',
      },
    };

    exportAndDownloadCSV({
      rows: [...csvRows, totalsRow],
      filteredHeaders: filteredHeaders,
      excludedHeaders: CSV_EXCLUDED_HEADER_IDS,
      fileName: `ManageDelinquentAndPrepaid_${propertyName
        ?.split(' ')
        .join('-')}_${formatDateDB(new Date())}.csv`,
      hasAnyFilters,
    });
  }, [filteredHeaders, rows, footerRow, hasAnyFilters, propertyName]);

  return { onCSVButtonClick };
};
